import { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import DefaultLayout from '../../../layouts/DefaultLayout';
import { Helmet } from 'react-helmet-async';
import SkeletonLoader from '../../../components/loader/SkeletonLoader';
import { RapportPdvDTO } from '../../../utils/types/objects/RapportPdvDTO';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../../services/api/apiClient';
import { formatAxiosError } from '../../../utils/formatAxiosError';
import { IClient } from '../../../utils/types/IClient';
import ClientSelect from '../../../components/select/ClientSelect';
import ChartLoadingSkeleton from '../../../components/loading/chart-loading-skeleton';
import LivraisonCarteReportGrid from '../../../components/grid/LivraisonCarteReportGrid';
import { LivraisonCartePdvChart } from '../../../components/chart/livraison-carte-pdv-chart';
import QueryErrorAlerts, { QueryErrorEntry } from '../../../components/others/QueryErrorAlerts';

function LivraisonCarteReport() {
    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    // ---

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: serverDate = null,
        refetch: refetchServerDate,
        isError: isServerDateError,
        error: serverDateError,
    } = useQuery<string>({
        queryKey: ['serverDate'],
        queryFn: async () => {
            const res = await apiClient.get(`/api/data/serverDt`);
            return res.data;
        },
        meta: { title: 'Chargement de la date serveur' },
        retry: 3,
        staleTime: 0,
    });

    const {
        data: livraisons,
        isLoading: isLivraisonsLoading,
        isError: isLivraisonsError,
        error: livraisonsError,
        refetch: loadLivraisons,
    } = useQuery<RapportPdvDTO[]>({
        queryKey: ['livraisonsCardReport', selectedCardCode, startDate, endDate],
        queryFn: async () => {
            if (!startDate || !endDate) return [];

            const response = await apiClient.get<RapportPdvDTO[]>('/api/rapport/get-deliveries-by-card-report', {
                params: {
                    codeClient: selectedCardCode,
                    dateDebut: startDate,
                    dateFin: endDate,
                },
            });

            return response.data ?? [];
        },
        meta: { title: 'Chargement des livraisons' },
        enabled: false,
        staleTime: 0,
    });

    // ---

    const handleLoadReport = async () => {
        if (!startDate || !endDate) {
            return;
        }

        await loadLivraisons();
    };

    // ---

    useEffect(() => {
        if (serverDate) {
            const serverDt = new Date(serverDate);

            const localYear = serverDt.getFullYear();
            const localMonth = serverDt.getMonth() + 1;
            const localDate = serverDt.getDate();

            const pad = (n: any) => n.toString().padStart(2, '0');

            const startDateString = `${localYear}-${pad(localMonth)}-01`;
            const endDateString = `${localYear}-${pad(localMonth)}-${pad(localDate)}`;

            setStartDate(startDateString);
            setEndDate(endDateString);
        }
    }, [serverDate]);

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isServerDateError, error: serverDateError, title: 'Erreur lors du chargement de la date serveur', refetch: refetchServerDate },
            { isError: isLivraisonsError, error: livraisonsError, title: 'Erreur lors du chargement des livraisons', refetch: loadLivraisons },
        ],
        [isClientsError, clientsError, refetchClients, isServerDateError, serverDateError, refetchServerDate, isLivraisonsError, livraisonsError, loadLivraisons],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Rapport livraison carte :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-wrapper">
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col">
                                <div className="page-pretitle">Rapport</div>
                                <h2 className="page-title">Rapport livraison carte</h2>
                            </div>

                            <div className="col-auto ms-auto d-print-none">
                                <div className="row gy-2 gx-3 align-items-center">
                                    <div className="col-auto">
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-calendar">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M16 2a1 1 0 0 1 .993 .883l.007 .117v1h1a3 3 0 0 1 2.995 2.824l.005 .176v12a3 3 0 0 1 -2.824 2.995l-.176 .005h-12a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-12a3 3 0 0 1 2.824 -2.995l.176 -.005h1v-1a1 1 0 0 1 1.993 -.117l.007 .117v1h6v-1a1 1 0 0 1 1 -1zm3 7h-14v9.625c0 .705 .386 1.286 .883 1.366l.117 .009h12c.513 0 .936 -.53 .993 -1.215l.007 -.16v-9.625z" />
                                                    <path d="M12 12a1 1 0 0  Coalition .883l.007 .117v3a1 1 0 0 1 -1.993 .117l-.007 -.117v-2a1 1 0 0 1 -.117 -1.993l.117 -.007h1z" />
                                                </svg>
                                            </span>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={startDate ?? ''}
                                                onChange={(e) => {
                                                    setStartDate(e.target.value);
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-auto">
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-calendar">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M16 2a1 1 0 0 1 .993 .883l.007 .117v1h1a3 3 0 0 1 2.995 2.824l.005 .176v12a3 3 0 0 1 -2.824 2.995l-.176 .005h-12a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-12a3 3 0 0 1 2.824 -2.995l.176 -.005h1v-1a1 1 0 0 1 1.993 -.117l.007 .117v1h6v-1a1 1 0 0 1 1 -1zm3 7h-14v9.625c0 .705 .386 1.286 .883 1.366l.117 .009h12c.513 0 .936 -.53 .993 -1.215l.007 -.16v-9.625z" />
                                                    <path d="M12 12a1 1 0 0 1 .993 .883l.007 .117v3a1 1 0 0 1 -1.993 .117l-.007 -.117v-2a1 1 0 0 1 -.117 -1.993l.117 -.007h1z" />
                                                </svg>
                                            </span>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={endDate ?? ''}
                                                onChange={(e) => {
                                                    setEndDate(e.target.value);
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-auto">
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-users-group">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" />
                                                    <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path d="M17 10h2a2 2 0 0 1 2 2v1" />
                                                    <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path d="M3 13v-1a2 2 0 0 1 2 -2h2" />
                                                </svg>
                                            </span>

                                            <ClientSelect clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                                        </div>
                                    </div>

                                    <div className="col-auto">
                                        <button
                                            className="btn btn-6 w-100 btn-icon"
                                            aria-label="Actualiser"
                                            onClick={() => {
                                                handleLoadReport();
                                            }}
                                            disabled={isLivraisonsLoading}
                                            type="button"
                                        >
                                            {isLivraisonsLoading ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                </>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-refresh">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                                                    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="page-body">
                    <div className="container-xl">
                        <QueryErrorAlerts queries={queryErrors} />

                        {livraisonsError && (
                            <div className="alert alert-danger" role="alert">
                                <div className="alert-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                        <path d="M12 9v4"></path>
                                        <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                        <path d="M12 16h.01"></path>
                                    </svg>
                                </div>
                                Une erreur est survenue lors du chargement des données. {formatAxiosError(livraisonsError)?.message}
                            </div>
                        )}

                        <div className="tab-pane" id="tabs-data">
                            {isLivraisonsLoading ? (
                                <>
                                    <p className="text-center">Chargement ...</p>
                                    <SkeletonLoader />
                                </>
                            ) : (
                                <>
                                    {(livraisons ?? []).length > 0 ? (
                                        <>
                                            <div className="card">
                                                <div className="card-header">
                                                    <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs">
                                                        <li className="nav-item">
                                                            <a href="#tabs-home-3" className="nav-link active" data-bs-toggle="tab">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-table">
                                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                    <path d="M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14z" />
                                                                    <path d="M3 10h18" />
                                                                    <path d="M10 3v18" />
                                                                </svg>
                                                                &nbsp;Données
                                                            </a>
                                                        </li>
                                                        <li className="nav-item">
                                                            <a href="#tabs-profile-3" className="nav-link" data-bs-toggle="tab">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-chart-bar">
                                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                    <path d="M3 13a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
                                                                    <path d="M15 9a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
                                                                    <path d="M9 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
                                                                    <path d="M4 20h14" />
                                                                </svg>
                                                                &nbsp;Graphique
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </div>
                                                <div className="card-body">
                                                    <div className="tab-content">
                                                        <div className="tab-pane active show" id="tabs-home-3">
                                                            <LivraisonCarteReportGrid items={livraisons ?? []} />
                                                        </div>
                                                        <div className="tab-pane" id="tabs-profile-3">
                                                            <div>
                                                                {isLivraisonsLoading ? (
                                                                    <ChartLoadingSkeleton />
                                                                ) : (
                                                                    <>
                                                                        <LivraisonCartePdvChart data={livraisons} isLoading={isLivraisonsLoading} />
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="alert alert-warning" role="alert">
                                            <div className="alert-icon">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                    <path d="M12 9v4"></path>
                                                    <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                    <path d="M12 16h.01"></path>
                                                </svg>
                                            </div>
                                            Aucune donnée.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

LivraisonCarteReport.Layout = DefaultLayout;

export default LivraisonCarteReport;
